import { useCallback } from 'react';
import { toast } from 'sonner';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const ArchiveClientMutation = graphql(/* GraphQL */ `
  mutation ArchiveHousehold($input: ArchiveHouseholdInput!) {
    archiveHousehold(input: $input) {
      success
    }
  }
`);

const UnarchiveClientMutation = graphql(/* GraphQL */ `
  mutation UnarchiveHousehold($input: UnarchiveHouseholdInput!) {
    unarchiveHousehold(input: $input) {
      success
    }
  }
`);

type Props = {
  readonly onArchive?: () => void;
  readonly onUnarchive?: () => void;
};

export function useHouseholdArchive({ onArchive, onUnarchive }: Props = {}) {
  const [archiveClient] = useMutationWithErrorLogging(ArchiveClientMutation);
  const [unarchiveClient] = useMutationWithErrorLogging(UnarchiveClientMutation);

  const handleArchive = useCallback(
    async (id: string, name: string) => {
      try {
        await archiveClient({
          variables: { input: { id } },
          update(cache) {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'advisorDashboardClients' });
            cache.gc();
          },
        });

        toast.success(`${name} has been archived`, {
          action: {
            label: 'Undo',
            onClick: () => handleUnarchive(id, name),
          },
        });

        onArchive?.();
      } catch (error) {
        console.error('Failed to archive client', error);
        handleMutationError(error, 'Failed to archive client');
      }
    },
    [archiveClient],
  );

  const handleUnarchive = useCallback(
    async (id: string, name: string) => {
      try {
        await unarchiveClient({
          variables: { input: { id } },
          update(cache) {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'advisorDashboardClients' });
            cache.gc();
          },
        });

        toast.success(`${name} has been restored to clients list`, {
          action: {
            label: 'Undo',
            onClick: () => handleArchive(id, name),
          },
        });

        onUnarchive?.();
      } catch (error) {
        console.error('Failed to restore client', error);
        handleMutationError(error, 'Failed to restore client');
      }
    },
    [unarchiveClient],
  );

  return {
    handleArchive,
    handleUnarchive,
  };
}
